import {Alert, Button, CircularProgress, InputAdornment, TextField, Typography} from "@mui/material";
import {FormEvent, useState} from "react";
import {useKeycloak} from "@react-keycloak/web";
import './ContactForm.scss';
import {config} from "../config";
import {LockIcon} from "./Icons";

type SendStatus = "idle" | "sending" | "success" | "error";

/** Additional required text field, sent in the request body under `name`. */
export interface ContactFormField {
    name: string,
    label: string,
    requiredMessage: string,
    placeholder?: string,
    helperText?: string,
    initialValue?: string,
    multiline?: boolean,
    uppercase?: boolean,
}

const DEFAULT_ERROR = "Nie udało się wysłać wiadomości. Spróbuj ponownie później.";

function validatePhone(phone: string): string | null {
    if (!phone.trim()) {
        return "Podaj numer telefonu";
    }
    const digits = phone.replace(/\D/g, "");
    if (!/^[+\d\s()-]+$/.test(phone.trim()) || digits.length < 9 || digits.length > 15) {
        return "Podaj poprawny numer telefonu";
    }
    return null;
}

function validateMessage(message: string): string | null {
    return message.trim() ? null : "Treść wiadomości jest wymagana";
}

function initialFieldValues(fields: ContactFormField[]): Record<string, string> {
    return Object.fromEntries(fields.map(f => [f.name, f.initialValue ?? ""]));
}

function LockedField(props: {label: string, value: string}) {
    return (
        <TextField
            className="ContactFormLocked"
            label={props.label}
            value={props.value}
            placeholder="Brak danych w profilu"
            fullWidth
            size="small"
            InputLabelProps={{shrink: true}}
            InputProps={{
                readOnly: true,
                endAdornment: (
                    <InputAdornment position="end">
                        <LockIcon fontSize="small"/>
                    </InputAdornment>
                ),
            }}
        />
    )
}

/**
 * Contact form posting `{phone, message, ...fields}` to `endpoint` on the e-Kuria server.
 * Parish name, address and e-mail are shown read-only - the server takes them from the JWT.
 */
function ContactForm(props: {
    endpoint: string,
    title: string,
    lead: string,
    successMessage: string,
    messagePlaceholder: string,
    submitLabel?: string,
    fields?: ContactFormField[],
}) {
    const {keycloak} = useKeycloak();
    const token = keycloak.tokenParsed;
    const fields = props.fields ?? [];

    const [phone, setPhone] = useState("");
    const [message, setMessage] = useState("");
    const [values, setValues] = useState(() => initialFieldValues(fields));
    const [touched, setTouched] = useState(false);
    const [status, setStatus] = useState<SendStatus>("idle");
    const [error, setError] = useState(DEFAULT_ERROR);

    const phoneError = validatePhone(phone);
    const messageError = validateMessage(message);
    const fieldErrors = Object.fromEntries(fields.map(f => [f.name, values[f.name].trim() ? null : f.requiredMessage]));
    const hasErrors = !!phoneError || !!messageError || Object.values(fieldErrors).some(e => e);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (hasErrors) {
            return;
        }

        setStatus("sending");
        let errorMessage = DEFAULT_ERROR;
        try {
            await keycloak.updateToken(30);
            const response = await fetch(`${config.ekuriaServerUrl}${props.endpoint}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${keycloak.token}`,
                },
                body: JSON.stringify({
                    phone: phone.trim(),
                    message: message.trim(),
                    ...Object.fromEntries(fields.map(f => [f.name, values[f.name].trim()])),
                }),
            });
            if (!response.ok) {
                const text = await response.text().catch(() => "");
                if (text && !text.trim().startsWith("{")) {
                    errorMessage = text;
                }
                throw new Error(errorMessage);
            }
            setPhone("");
            setMessage("");
            setValues(initialFieldValues(fields));
            setTouched(false);
            setStatus("success");
        } catch {
            setError(errorMessage);
            setStatus("error");
        }
    };

    return (
        <form className="ContactForm" onSubmit={handleSubmit} noValidate>
            <Typography variant="h6" className="ContactFormTitle">{props.title}</Typography>
            <Typography className="ContactFormLead">{props.lead}</Typography>

            <div className="ContactFormLockedGroup">
                <LockedField label="Nazwa" value={token?.displayed_name ?? ""}/>
                <LockedField label="Adres" value={token?.displayed_address ?? ""}/>
                <LockedField label="Adres e-mail" value={token?.email ?? ""}/>
                <Typography className="ContactFormHint">Dane pobrane z konta e-Kuria</Typography>
            </div>

            <TextField
                label="Numer telefonu"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                error={touched && !!phoneError}
                helperText={touched && phoneError}
                required
                fullWidth
                size="small"
                margin="dense"
            />
            {fields.map(field =>
                <TextField
                    key={field.name}
                    label={field.label}
                    value={values[field.name]}
                    onChange={e => setValues({
                        ...values,
                        [field.name]: field.uppercase ? e.target.value.toUpperCase() : e.target.value,
                    })}
                    error={touched && !!fieldErrors[field.name]}
                    helperText={(touched && fieldErrors[field.name]) || field.helperText}
                    placeholder={field.placeholder}
                    required
                    fullWidth
                    multiline={field.multiline}
                    minRows={field.multiline ? 2 : undefined}
                    size="small"
                    margin="dense"
                />
            )}
            <TextField
                label="Treść wiadomości"
                value={message}
                onChange={e => setMessage(e.target.value)}
                error={touched && !!messageError}
                helperText={touched && messageError}
                placeholder={props.messagePlaceholder}
                required
                fullWidth
                multiline
                minRows={4}
                margin="dense"
            />

            {status === "success" &&
                <Alert severity="success" className="ContactFormAlert" onClose={() => setStatus("idle")}>
                    {props.successMessage}
                </Alert>
            }
            {status === "error" &&
                <Alert severity="error" className="ContactFormAlert" onClose={() => setStatus("idle")}>
                    {error}
                </Alert>
            }

            <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={status === "sending"}
                className="ContactFormSubmit"
                startIcon={status === "sending" ? <CircularProgress size={18} color="inherit"/> : undefined}
            >
                {status === "sending" ? "Wysyłanie…" : props.submitLabel ?? "Wyślij zapytanie"}
            </Button>
        </form>
    )
}

export default ContactForm;
